import {
    buildFuelPriceRows,
    buildActiveFuelDetailsMap,
    extractFuelPriceData,
    FUEL_TYPE_OPTIONS,
    getFuelTypeLabel,
} from '../priceRegistry/fuelPriceMapper';
import {
    extractEvTariffData,
    normalizeEvTariffRows,
    getActiveCurrentEvTariffRows,
    TARIFF_TYPE_OPTIONS,
} from '../priceRegistry/evTariffMapper';

export const REGISTRY_KIND_FUEL = 'fuel';
export const REGISTRY_KIND_EV = 'ev';

const TARIFF_VALUE_SET = new Set(TARIFF_TYPE_OPTIONS.map(({ value }) => value));

const formatPriceNumber = (value) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return null;
    return value.toFixed(2);
};

/** Build dropdown options and price lookup from current registry API responses. */
export const buildRegistryFuelTariffState = (fuelApiResponse, evApiResponse) => {
    const fuelRows = buildFuelPriceRows(extractFuelPriceData(fuelApiResponse));
    const fuelDetails = buildActiveFuelDetailsMap(fuelRows);

    const evRows = getActiveCurrentEvTariffRows(
        normalizeEvTariffRows(extractEvTariffData(evApiResponse)),
    );
    const evByTariff = evRows.reduce((accumulator, row) => {
        if (row.tariff) accumulator[row.tariff] = row;
        return accumulator;
    }, {});

    const options = [];
    const priceByValue = {};

    FUEL_TYPE_OPTIONS.forEach(({ value, label }) => {
        const detail = fuelDetails[value];
        if (detail?.price == null || !Number.isFinite(detail.price)) return;
        options.push({ value, label, kind: REGISTRY_KIND_FUEL });
        priceByValue[value] = {
            price: detail.price,
            kind: REGISTRY_KIND_FUEL,
            unit: 'RM/L',
        };
    });

    TARIFF_TYPE_OPTIONS.forEach(({ value, label }) => {
        const row = evByTariff[value];
        if (row?.rate == null || !Number.isFinite(row.rate)) return;
        options.push({ value, label, kind: REGISTRY_KIND_EV });
        priceByValue[value] = {
            price: row.rate,
            kind: REGISTRY_KIND_EV,
            unit: 'RM/kWh',
        };
    });

    return { options, priceByValue };
};

/** Keep a saved preset value visible even if it is no longer in the active registry snapshot. */
export const mergeFuelTariffOptions = (registryOptions, savedValue) => {
    const trimmed = String(savedValue || '').trim();
    if (!trimmed) return registryOptions;
    if (registryOptions.some((option) => option.value === trimmed)) {
        return registryOptions;
    }
    const tariffLabel = TARIFF_TYPE_OPTIONS.find((option) => option.value === trimmed)?.label;
    const fuelLabel = getFuelTypeLabel(trimmed);
    const fallbackLabel = tariffLabel || (fuelLabel !== trimmed ? fuelLabel : trimmed);
    return [{ value: trimmed, label: fallbackLabel }, ...registryOptions];
};

export const formatRegistryActivePrice = (selectedValue, priceByValue, { loading = false } = {}) => {
    if (loading) return 'Loading…';
    const trimmed = String(selectedValue || '').trim();
    if (!trimmed) return '';
    const entry = priceByValue?.[trimmed];
    if (!entry || entry.price == null) return '—';
    const formatted = formatPriceNumber(entry.price);
    if (!formatted) return '—';
    return `${formatted} ${entry.unit}`;
};

export const isRegistryFuelOrTariffValue = (value, validValues) => {
    const trimmed = String(value || '').trim();
    if (!trimmed) return false;
    if (validValues instanceof Set) return validValues.has(trimmed);
    if (Array.isArray(validValues)) return validValues.includes(trimmed);
    return true;
};

/** True when the registry dropdown value is an EV charging tariff (DC Fast, AC Home, …). */
export const isRegistryEvTariffValue = (value) => {
    const trimmed = String(value || '').trim();
    return Boolean(trimmed) && TARIFF_VALUE_SET.has(trimmed);
};
